import { Trans, useLingui } from '@lingui/react/macro';
import { CheckmarkCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useEffect, useMemo, useState } from 'react';

import { ButtonClicked, ComponentRendered } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type {
  CloudConnectSubscription,
  Plan,
  Price,
} from 'src/api/endpoints/cloudConnect.types';
import {
  useCloudConnectSubscription,
  useUpdateCloudConnectBillingInterval,
} from 'src/api/queries';
import { Button, Modal, NotificationBox } from 'src/components';
import { Link2 } from 'src/components/Link2';
import { LinkExternal } from 'src/components/LinkExternal';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import {
  allowBiennialDirectLink,
  useOfferExpirationDetails,
} from '../utils/getOfferExpirationDetails';
import { BiennialProratedPrice } from './BiennialProratedPrice';

export const CloudConnectChangePaymentIntervalToBiennialForm = ({
  biennialPrice: biennialPlanPrice,
  currentPlan,
  hasPendingScheduleToBiennial = false,
  subscription,
}: {
  biennialPrice: Price | null;
  currentPlan: Plan;
  /**
   * True if the customer isn't currently on biennial billing, but has a Stripe subscription schedule (left over from
   * a since-fixed bug) that will incorrectly move them to biennial at the end of their current period instead of
   * applying immediately. Submitting the form in this state re-runs the (now fixed) switch-to-biennial flow, which
   * releases the stale schedule and applies biennial correctly with proration. Bypasses the "offer has ended" block
   * so these customers can still get to the confirm screen (shown with the same copy as a normal switch), even
   * outside the campaign window, even when direct-link access is disabled.
   */
  hasPendingScheduleToBiennial?: boolean;
  subscription: CloudConnectSubscription;
}) => {
  const { t, i18n } = useLingui();
  const updatePaymentInterval = useUpdateCloudConnectBillingInterval();
  const { offerHasEnded, daysUntilOfferEnds, offerEndDate } =
    useOfferExpirationDetails();
  const offerEndDateFormatted = formatDate(offerEndDate, {
    locale: i18n.locale,
  });
  const nextBillingDate = formatDate(subscription.periodEnd, {
    fallback: t`No invoice available`,
    locale: i18n.locale,
  });
  const confirmText = t`Switch to 2-year plan`;
  const invoicePrice = subscription.nextInvoice?.amountDue;
  // True once the customer has clicked through to Stripe to approve the payment. Used to show a loading state on
  // the "Approve payment" button, so it's obvious we're checking for the payment result in the background rather
  // than the app having gone silent.
  const [hasClickedApprovePayment, setHasClickedApprovePayment] =
    useState(false);

  useEffect(() => {
    tracker.trackEvent(
      new ComponentRendered({
        component_name: 'CloudConnectChangePaymentIntervalToBiennialForm',
        component_type: 'change payment interval form',
      })
    );
  }, []);

  const onSubmit = () => {
    tracker.trackEvent(
      new ButtonClicked({
        component_location:
          ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
        text: confirmText,
        action: 'confirm change payment interval',
      })
    );

    updatePaymentInterval.mutate('biennial');
  };

  // True once the customer has approved the out-of-band payment action (e.g. 3D Secure) and the subscription has
  // refetched to confirm biennial billing is now active. Used below to show the success screen instead of either
  // the "approve payment" screen or the "already on 2-year billing" screen.
  const hasCompletedPendingPayment =
    updatePaymentInterval.data?.status === 'requires_payment' &&
    currentPlan.interval === 'biennial';

  const isAwaitingPaymentApproval =
    updatePaymentInterval.data?.status === 'requires_payment' &&
    !hasCompletedPendingPayment;

  // Only start polling once the customer has actually clicked through to Stripe - polling from the moment the
  // screen is shown (before they've done anything) would just be wasted requests. Keeping this extra observer on
  // the same query key means its refetches update the shared cache, so the route-level subscription query (which
  // supplies `subscription`/`currentPlan` here) picks up the change automatically - no extra plumbing needed.
  const shouldPollForPaymentUpdates =
    isAwaitingPaymentApproval && hasClickedApprovePayment;

  useCloudConnectSubscription({
    enabled: shouldPollForPaymentUpdates,
    pollForUpdates: shouldPollForPaymentUpdates,
  });

  const paymentUrl =
    updatePaymentInterval.data?.status === 'requires_payment'
      ? updatePaymentInterval.data.paymentUrl
      : undefined;

  // Memoized so this element keeps the same reference across re-renders triggered purely by the subscription
  // polling above (which don't change `paymentUrl` or `hasClickedApprovePayment`). Ark's `Button` re-measures its
  // width whenever its `children` prop changes identity while `loading`, so passing a fresh `<Trans>` element on
  // every poll-triggered render made the "Approve payment" label flash back into view every few seconds.
  const approvePaymentButton = useMemo(() => {
    if (!paymentUrl) return null;

    return (
      <Button
        as="a"
        to={paymentUrl}
        variant="primary"
        className="w-full xs:w-fit"
        data-cy="approve-payment-button"
        loading={hasClickedApprovePayment}
        onClick={() => setHasClickedApprovePayment(true)}
        analytics={{
          location: ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
          action: 'approve pending payment',
        }}
      >
        <Trans>Approve payment</Trans>
      </Button>
    );
  }, [paymentUrl, hasClickedApprovePayment]);

  if (
    updatePaymentInterval.data?.status === 'requires_payment' &&
    !hasCompletedPendingPayment
  ) {
    return (
      <div className="flex flex-col gap-24">
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-sm">
            <Trans>Approve your payment</Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            {paymentUrl ? (
              <Trans>
                Your bank requires you to manually approve this payment. Approve
                it in the window that opens, then come back here.
              </Trans>
            ) : (
              <Trans>
                Your bank requires you to manually approve this payment. Check
                your email for a message from Stripe with instructions, or&nbsp;
                <LinkExternal
                  inline
                  to={SUPPORT_URL.CONTACT_SUPPORT}
                  componentLocation={
                    ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
                  }
                >
                  contact Support
                </LinkExternal>
                &nbsp;if you need help.
              </Trans>
            )}
          </Typography>
        </Modal.Description>

        <div className="mt-24 flex w-full flex-col flex-wrap justify-center gap-16 xs:flex-row">
          <Modal.Close asChild>
            <Button
              variant="secondary"
              className="w-full xs:w-fit"
              data-cy="close-payment-approval-button"
              analytics={{
                location:
                  ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
                action: 'close change payment interval modal',
              }}
            >
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>

          {approvePaymentButton}
        </div>
      </div>
    );
  }

  if (!invoicePrice) {
    return (
      <NotificationBox variant="error" title={t`Something went wrong`}>
        <Trans>
          No payment or invoice information available. Please try again later or
          contact support if the problem persists.
        </Trans>
      </NotificationBox>
    );
  }

  const currentPrice = formatAmount(
    invoicePrice.totalAmount,
    invoicePrice.currency,
    i18n.locale
  );

  if (
    updatePaymentInterval.data?.status === 'success' ||
    hasCompletedPendingPayment
  ) {
    return (
      <>
        <CheckmarkCircle
          data-cy="payment-interval-confirmation-icon"
          size={86}
          className="mx-auto mb-24 text-feedback-green-500"
        />
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-xs">
            <Trans>
              You&apos;ve switched to{' '}
              <span className="whitespace-nowrap">2-year billing</span>
            </Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md" className="mt-16">
            <Trans>
              You&apos;ll be charged {currentPrice} on{' '}
              <span className="font-bold whitespace-nowrap">
                {nextBillingDate}
              </span>
            </Trans>
          </Typography>
        </Modal.Description>

        <Modal.Close asChild>
          <Button
            className="mx-auto mt-24 w-full xs:w-auto"
            data-cy="close-payment-interval-confirmation-button"
          >
            <Trans>Close</Trans>
          </Button>
        </Modal.Close>
      </>
    );
  }

  if (currentPlan.interval === 'biennial') {
    return (
      <div className="flex flex-col gap-24">
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-sm">
            <Trans>Already on 2-year billing</Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans>
              You are currently on the 2-year billing plan. Looking to switch to
              monthly?
            </Trans>
          </Typography>
        </Modal.Description>

        <div className="flex w-full justify-center gap-8">
          <Modal.Close asChild>
            <Button variant="secondary">
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>

          <Link2
            as="button"
            variant="primary"
            to="."
            search={{ updateBillingInterval: 'monthly' }}
            componentLocation={
              ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
            }
          >
            <Trans>Switch to monthly</Trans>
          </Link2>
        </div>
      </div>
    );
  }

  if (
    offerHasEnded && // Dutside of campaign window
    !allowBiennialDirectLink && // Do not allow direct access to the biennial modal (link, not button)
    !hasPendingScheduleToBiennial // Some customers got biennial scheduled by mistake, and should be allowed to switch immediately
  ) {
    if (currentPlan.interval !== 'monthly') {
      return (
        <div>
          <Typography as="h2" variant="heading-sm">
            <Trans>This offer has expired</Trans>
          </Typography>

          <Typography as="p" variant="body-md" className="mt-16">
            <Trans>
              This limited-time offer has ended.
              <br />
              Your account will remain on standard annual billing.
            </Trans>
          </Typography>

          <div className="flex w-full justify-center gap-8">
            <Modal.Close asChild>
              <Button className="mt-16" variant="primary">
                <Trans>Close</Trans>
              </Button>
            </Modal.Close>
          </div>
        </div>
      );
    }

    return (
      <div>
        <Typography as="h2" variant="heading-sm">
          <Trans>This offer has expired</Trans>
        </Typography>

        <Typography as="p" variant="body-md" className="mt-16">
          <Trans>
            This offer has ended, but you can still save by switching to our
            annual plan instead of paying monthly.
          </Trans>
        </Typography>

        <div className="mt-24 flex w-full items-center justify-center gap-8">
          <Modal.Close asChild>
            <Button variant="secondary">
              <Trans>No, thanks</Trans>
            </Button>
          </Modal.Close>

          <Link2
            as="button"
            variant="primary"
            size="large"
            to="/subscriptions/payment"
            from="/subscriptions/payment"
            search={{ updateBillingInterval: 'annual' }}
            componentLocation={
              ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
            }
          >
            <Trans>See annual pricing</Trans>
          </Link2>
        </div>
      </div>
    );
  }

  if (!biennialPlanPrice) {
    return (
      <div>
        <Typography as="h2" variant="heading-sm">
          <Trans>2-year billing unavailable</Trans>
        </Typography>
        <Typography as="p" variant="body-md" className="mt-16">
          <Trans>
            The 2-year billing option is currently unavailable. Please try again
            later or contact support if the problem persists.
          </Trans>
        </Typography>
        <div className="flex w-full justify-center gap-8">
          <Modal.Close asChild>
            <Button className="mt-16" variant="primary">
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>
        </div>
      </div>
    );
  }

  const biennialPrice = formatAmount(
    biennialPlanPrice.totalAmount,
    biennialPlanPrice.currency,
    i18n.locale
  );

  return (
    <>
      <Modal.Title asChild>
        <Typography as="h2" variant="heading-xs">
          <Trans>Switch to our 2-year plan</Trans>
        </Typography>
      </Modal.Title>

      <div className="my-16">
        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            {offerHasEnded ? (
              <Trans>
                You can get 2 years of Connect for just{' '}
                <strong>{biennialPrice}</strong>.
                <br />
                That&apos;s 6 months free.
              </Trans>
            ) : (
              <Trans>
                For the next {daysUntilOfferEnds} days, you can get 2 years of
                Connect for just <strong>{biennialPrice}</strong>.
                <br />
                That&apos;s 6 months free.
              </Trans>
            )}
          </Typography>
        </Modal.Description>
      </div>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>
          We&apos;ll credit your remaining subscription time toward your new
          plan, so you&apos;ll only be charged <BiennialProratedPrice /> today.
        </Trans>
      </Typography>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>
          Automatically renews at <strong>{biennialPrice}</strong> every 2
          years.
        </Trans>
      </Typography>

      {!offerHasEnded && (
        <Typography as="p" variant="body-md" className="mb-32">
          <Trans>
            Offer ends{' '}
            <strong data-cy="change-payment-next-billing-date">
              {offerEndDateFormatted}
            </strong>
          </Trans>
        </Typography>
      )}

      {updatePaymentInterval.isError && (
        <NotificationBox
          error={updatePaymentInterval.error}
          className="mt-16"
          title={t`Something went wrong`}
          data-cy="error-message"
        >
          <span className="mt-4">
            <Trans>
              Please try again. If the problem keeps happening,&nbsp;
              <LinkExternal
                inline
                to={SUPPORT_URL.CONTACT_SUPPORT}
                componentLocation={
                  ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
                }
              >
                contact Support
              </LinkExternal>
              .
            </Trans>
          </span>
        </NotificationBox>
      )}

      <div className="mt-24 flex w-full flex-col flex-wrap justify-center gap-16 xs:flex-row">
        <Modal.Close asChild>
          <Button
            type="button"
            variant="secondary"
            className="w-full xs:w-fit"
            disabled={updatePaymentInterval.isPending}
            data-cy="cancel-change-payment-interval-button"
          >
            <Trans>Cancel</Trans>
          </Button>
        </Modal.Close>

        <Button
          onClick={onSubmit}
          loading={updatePaymentInterval.isPending}
          className="w-full xs:w-fit"
          data-cy="confirm-change-payment-interval-button"
          variant="primary"
        >
          <span>{confirmText}</span>
        </Button>
      </div>
    </>
  );
};
